import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowNarrowLeftIcon, ArrowNarrowRightIcon } from '@heroicons/react/solid';

import { Test } from '../../entities/test';
import TestStatusBadge from './status-badge';
import Badge from '../common/badge';
import { getAgeFromBirthDate } from '../../services/formatter/date';
import { parameters } from '../../constants/parameters';

interface ComponentProps {
  tests: Test[];
  pages: number;
  onUpdate: (pageNo: number) => void;
  currPageNo: number;
}

export default function TestsTableComponent({ tests, pages, onUpdate, currPageNo }: ComponentProps) {
  const onNextPage = () => {
    if (currPageNo < pages) {
      onUpdate(currPageNo + 1);
    }
  };

  const onPrevPage = () => {
    if (currPageNo > 1) {
      onUpdate(currPageNo - 1);
    }
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-4 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Test ID
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Patient
                    </th>
                    <th
                      scope="col"
                      className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Country
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Product
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Doctor
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Days left
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Test Results
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Questionnaire
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Order
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tests.map((test, index) => (
                    <tr key={test.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                        <Link to={`/tests/results/${test.id}`} className="text-indigo-600 hover:text-indigo-900">
                          #{test.id}
                        </Link>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {test.patientName && test.patientLastName ? (
                          <div>
                            <div>
                              {test.patientHash ? (
                                <Link
                                  to={`/patients/preview/${test.patientHash}`}
                                  className="text-indigo-600 hover:text-indigo-900"
                                >
                                  {test.patientName} {test.patientLastName}
                                </Link>
                              ) : (
                                <span>
                                  {test.patientName} {test.patientLastName}
                                </span>
                              )}
                            </div>
                            {(test.patientAge !== undefined || test.patientBirthDate) && (
                              <div className="text-xs text-gray-500">
                                Age:{' '}
                                {test.patientBirthDate ? getAgeFromBirthDate(test.patientBirthDate) : test.patientAge}
                              </div>
                            )}
                            {test.patientIsChild && <Badge text="Child" status="primary" />}
                          </div>
                        ) : (
                          test.orderEmail || 'N/A'
                        )}
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-500">{test.countryCode || '-'}</td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        <div>{test.productTitle || test.productKey || 'N/A'}</div>
                        {test.isAtHome && <Badge text="At Home" status="success" />}
                        {test.hasConsultation && <Badge text="+ Consultation" status="primary" />}
                        {test.hasExtendedInterpretation && <Badge text="+ Interpretation" status="primary" />}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <TestStatusBadge status={test.status} />
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {test.administratorFirstName && test.administratorLastName
                          ? `${test.administratorFirstName} ${test.administratorLastName}`
                          : 'Unassigned'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {test.daysToDeadline !== null && test.daysToDeadline !== undefined ? test.daysToDeadline : '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {test.hasPositiveResults !== undefined && test.hasPositiveResults !== null ? (
                          <Badge
                            text={test.hasPositiveResults ? 'Positive' : 'Negative'}
                            status={test.hasPositiveResults ? 'error' : 'success'}
                          />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {test.questionnaireFilled !== undefined ? (
                          <Badge
                            text={test.questionnaireFilled ? 'Filled' : 'Missing'}
                            status={test.questionnaireFilled ? 'success' : 'error'}
                          />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <Link to={`/orders/results/${test.orderId}`} className="text-indigo-600 hover:text-indigo-900">
                          #{test.orderId}
                        </Link>
                        {test.shopifyOrderNumber && (
                          <div className="text-xs">
                            <span className="text-gray-400">Shopify: </span>
                            <a
                              href={`https://admin.shopify.com/store/${parameters.shopifyAdminStore}/orders/${test.shopifyOrderId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              #{test.shopifyOrderNumber}
                            </a>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {test.orderCreatedAt?.slice(0, 10) || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <nav className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="hidden sm:block">
            <p className="text-sm text-gray-700">
              Page <span className="font-medium">{currPageNo}</span> of <span className="font-medium">{pages}</span>
            </p>
          </div>
          <div className="flex-1 flex justify-between sm:justify-end">
            <button
              type="button"
              onClick={onPrevPage}
              disabled={currPageNo === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowNarrowLeftIcon className="h-5 w-5 mr-2" />
              Previous
            </button>
            <button
              type="button"
              onClick={onNextPage}
              disabled={currPageNo === pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ArrowNarrowRightIcon className="h-5 w-5 ml-2" />
            </button>
          </div>
        </nav>
      )}
    </>
  );
}
